import { Injectable, inject } from '@angular/core';
import { isFormulaFolder, OrdersActionLevel, resolveFormulaRootFolder } from '@app/orders/models/orders.model';
import { OrdersPaymentUtilsService } from '@app/orders/services/orders-payment-utils.service';
import { OrdersOperationsService } from '@app/orders/services/orders-operations.service';
import { OrdersSeatsService } from '@app/orders/services/orders-seats.service';
import { OrdersService } from '@app/orders/services/orders.service';
import { AlertService } from '@core/services/alert.service';
import {
  ApiException,
  CustomerPropertyEnumRequestDto,
  FileResponse,
  GetOrderItemDetailsResponseDto,
  Operation,
  OperatorTypeEnumRequestDto,
  OrderArticleProductResponseDto,
  OrderArticleSeatResponseDto,
  OrderItemHistoryResponseDto,
  OrderItemPaymentResponseDto,
  PaymentOperationTypeEnumRequestDto,
} from '@core/services/apiclient';
import { Actions, createEffect, ofType } from '@ngrx/effects';
import { Action, select, Store } from '@ngrx/store';
import { TranslateService } from '@ngx-translate/core';
import * as fromOrders from '@root-store/orders-store';
import { TableFilterTypeEnum } from '@shared/models/table-filter.model';
import { Utils } from '@shared/utils/utils';
import { of } from 'rxjs';
import { catchError, concatMap, map, switchMap, withLatestFrom } from 'rxjs/operators';
import * as OrdersActions from '../actions/orders.actions';

/**
 * Libellé du toast de succès, par périmètre puis par action. Annuler une formule et annuler une
 * place sur toutes ses séances laissent la commande dans deux états différents : l'opérateur lit
 * lequel des deux vient de se produire, il n'a pas à le déduire de l'écran qu'il regarde.
 */
const SubscriptionActionToastKeys: Record<
  'formula' | OrdersActions.SubscriptionActionScope,
  Record<'cancel' | 'refund', string>
> = {
  formula: {
    cancel: 'orders.info.formulaCanceledSuccessfully',
    refund: 'orders.info.formulaRefundedSuccessfully',
  },
  [OrdersActions.SUBSCRIPTION_SEATS_SCOPE]: {
    cancel: 'orders.info.formulaSeatsCanceledSuccessfully',
    refund: 'orders.info.formulaSeatsRefundedSuccessfully',
  },
};

@Injectable()
export class OrdersEffects {
  private readonly actions$ = inject(Actions);
  private readonly alertService = inject(AlertService);
  private readonly ordersService = inject(OrdersService);
  private readonly ordersSeatsService = inject(OrdersSeatsService);
  private readonly ordersOperationsService = inject(OrdersOperationsService);
  private readonly paymentService = inject(OrdersPaymentUtilsService);
  private readonly store = inject<Store<fromOrders.State>>(Store);
  private readonly utils = inject(Utils);
  private readonly translate = inject(TranslateService);

  loadOrderItems$ = createEffect(() => {
    return this.actions$.pipe(
      ofType(OrdersActions.loadOrderItems),
      switchMap(({ orderId, orderItemId, subscriptionOrderId }) =>
        this.ordersService.getOrderItems(orderId).pipe(
          concatMap((orderItems: GetOrderItemDetailsResponseDto[]) => {
            if (orderItems?.length) {
              // Une formule est affichée comme UN dossier : on sélectionne toujours sa racine
              // (`resolveFormulaRootFolder`), la même que celle vers laquelle le volet revient quand
              // on reclique sur la formule. Sans ça, l'ouverture et le retour désignaient deux
              // séances différentes — donc deux commentaires différents (GEN-7588).
              const toFormulaRoot = (item: GetOrderItemDetailsResponseDto | undefined) =>
                (isFormulaFolder(item) ? resolveFormulaRootFolder(orderItems, item.packageId) : undefined) ?? item;
              const formulaItem = toFormulaRoot(orderItems.find((i) => isFormulaFolder(i)));
              let orderItem: GetOrderItemDetailsResponseDto;

              if (subscriptionOrderId != null) {
                orderItem = toFormulaRoot(orderItems.find((i) => i.packageId === subscriptionOrderId || i.packageRowId === subscriptionOrderId)) ?? formulaItem ?? orderItems[0];
              } else if (orderItemId != null) {
                // Un id de dossier explicite est respecté tel quel : c'est une séance choisie par
                // l'utilisateur (double-clic dans la liste) ou le dossier à ré-afficher après une action.
                orderItem = orderItems.find(({ id }) => id === orderItemId) ?? formulaItem ?? orderItems[0];
              } else if (formulaItem) {
                orderItem = formulaItem;
              } else {
                orderItem = orderItems[0];
              }
              return [
                OrdersActions.loadOrderItemsSuccess({ orderItems }),
                OrdersActions.selectOrderItem({ orderItem }),
              ];
            }
            return [OrdersActions.loadOrderItemsSuccess({ orderItems })];
          }),
          catchError(() => of(OrdersActions.loadOrderItemsFailure())),
        ),
      ),
    );
  });

  selectOrderItem$ = createEffect(() => {
    return this.actions$.pipe(
      ofType(OrdersActions.selectOrderItem),
      concatMap(({ orderItem }) => [
        OrdersActions.loadOrderItemSeats({ orderItemId: orderItem?.id }),
        OrdersActions.loadOrderItemProducts({ orderItemId: orderItem?.id }),
      ]),
    );
  });

  loadOrderItemSeats$ = createEffect(() => {
    return this.actions$.pipe(
      ofType(OrdersActions.loadOrderItemSeats),
      switchMap(({ orderItemId }) =>
        this.ordersService.getOrderItemsSeats(orderItemId).pipe(
          concatMap((orderItemSeats: OrderArticleSeatResponseDto[]) => [
            OrdersActions.loadOrderItemSeatsSuccess({ orderItemSeats }),
          ]),
          catchError(() => of(OrdersActions.loadOrderItemSeatsFailure())),
        ),
      ),
    );
  });

  loadOrderItemProducts$ = createEffect(() => {
    return this.actions$.pipe(
      ofType(OrdersActions.loadOrderItemProducts),
      switchMap(({ orderItemId }) =>
        this.ordersService.getOrderItemsProducts(orderItemId).pipe(
          concatMap((orderItemProducts: OrderArticleProductResponseDto[]) => [
            OrdersActions.loadOrderItemProductsSuccess({ orderItemProducts }),
          ]),
          catchError(() => of(OrdersActions.loadOrderItemProductsFailure())),
        ),
      ),
    );
  });

  loadOrderItemPayments$ = createEffect(() => {
    return this.actions$.pipe(
      ofType(OrdersActions.loadOrderItemPayments),
      switchMap(({ orderItemId }) =>
        this.ordersService.getOrderItemsPayments(orderItemId).pipe(
          concatMap((orderItemPayments: OrderItemPaymentResponseDto[]) => [
            OrdersActions.loadOrderItemPaymentsSuccess({ orderItemPayments }),
          ]),
          catchError(() => of(OrdersActions.loadOrderItemPaymentsFailure())),
        ),
      ),
    );
  });

  loadOrderItemHistory$ = createEffect(() => {
    return this.actions$.pipe(
      ofType(OrdersActions.loadOrderItemHistory),
      switchMap(({ orderItemId, includeFormulaSeances }) =>
        this.ordersService.getOrderItemsHistory(orderItemId, includeFormulaSeances).pipe(
          concatMap((orderItemHistory: OrderItemHistoryResponseDto[]) => [
            OrdersActions.loadOrderItemHistorySuccess({ orderItemHistory }),
          ]),
          catchError(() => of(OrdersActions.loadOrderItemHistoryFailure())),
        ),
      ),
    );
  });

  processOrderPayment$ = createEffect(() => {
    return this.actions$.pipe(
      ofType(OrdersActions.processOrderPayment),
      switchMap(({ paymentInformation, orderId, level }) =>
        this.paymentService.PayOrderItems(paymentInformation).pipe(
          concatMap((result) => {
            if (result?.isOperationSuccess) {
              return [
                OrdersActions.processOrderPaymentSuccess({
                  orderItemId: paymentInformation?.orderItemIds?.[0],
                  orderId,
                  level,
                  isMultipleFolders: paymentInformation?.orderItemIds?.length > 1,
                }),
              ];
            } else {
              // Contrat legacy Operation (pas de ProblemDetails => pas de traceId possible).
              // TODO: basculer sur showApiError quand PayOrderItems sera migré UseCase.
              this.alertService.showCustom(
                this.translate.instant(
                  level === OrdersActionLevel.OrderDetails
                    ? 'orders.info.orderNumberCouldNotBePaid'
                    : level === OrdersActionLevel.FolderDetails
                    ? 'orders.info.folderCouldNotBePaid'
                    : 'orders.info.yourPaymentCouldNotBeMade',
                  { orderId: orderId },
                ),
                'danger',
              );
              return of({ type: 'EMPTY' });
            }
          }),
          catchError((err: unknown) => {
            this.alertService.showApiError(
              err,
              this.translate.instant(
                level === OrdersActionLevel.OrderDetails
                  ? 'orders.info.orderNumberCouldNotBePaid'
                  : level === OrdersActionLevel.FolderDetails
                  ? 'orders.info.folderCouldNotBePaid'
                  : 'orders.info.yourPaymentCouldNotBeMade',
                { orderId: orderId },
              ),
            );
            return of({ type: 'EMPTY' });
          }),
        ),
      ),
    );
  });

  // Paiement d'une formule entière depuis le volet (GEN-6320) : endpoint souscription-aware. Le
  // backend n'encaisse que le sous-ensemble réservé et ignore les places déjà payées → un statut
  // partiel (207) est un succès fonctionnel (on rafraîchit et on enchaîne le succès). Les 4xx sont
  // surfacés via showApiError (traceId du ProblemDetails).
  processSubscriptionPayment$ = createEffect(() => {
    return this.actions$.pipe(
      ofType(OrdersActions.processSubscriptionPayment),
      switchMap(({ request, orderId, orderItemId, level }) =>
        this.ordersService.payOrderItemsAndSubscriptions(request).pipe(
          // Le client NSwag jette pour tout statut hors 200 (le 207 arrive par catchError) :
          // une réponse non jetée est donc toujours un succès.
          concatMap(() => [
            OrdersActions.processOrderPaymentSuccess({
              orderItemId,
              orderId,
              level,
              isMultipleFolders: true,
            }),
          ]),
          catchError((error: unknown) => {
            const isPartialSuccess = ApiException.isApiException(error) && error.status === 207;
            if (isPartialSuccess) {
              return [
                OrdersActions.processOrderPaymentSuccess({
                  orderItemId,
                  orderId,
                  level,
                  isMultipleFolders: true,
                }),
              ];
            }
            this.alertService.showApiError(error, 'orders.info.folderCouldNotBePaid');
            return of({ type: 'EMPTY' });
          }),
        ),
      ),
    );
  });

  // Annulation d'une formule depuis le volet (GEN-6319) : endpoint souscription-aware. Le backend ne
  // libère que le sous-ensemble réservé → un statut partiel (207) est un succès fonctionnel. Les 4xx
  // sont surfacés via showApiError (traceId du ProblemDetails).
  processSubscriptionCancel$ = createEffect(() => {
    return this.actions$.pipe(
      ofType(OrdersActions.processSubscriptionCancel),
      switchMap(({ request, orderId, orderItemId, level }) =>
        this.ordersService.cancelOrderItemsAndSubscriptions(request).pipe(
          // Le client NSwag jette pour tout statut hors 200 (le 207 arrive par catchError) :
          // une réponse non jetée est donc toujours un succès.
          concatMap(() => [
            OrdersActions.processSubscriptionActionSuccess({ orderItemId, orderId, level, action: 'cancel' }),
          ]),
          catchError((error: unknown) => {
            const isPartialSuccess = ApiException.isApiException(error) && error.status === 207;
            if (isPartialSuccess) {
              return [
                OrdersActions.processSubscriptionActionSuccess({ orderItemId, orderId, level, action: 'cancel' }),
              ];
            }
            this.alertService.showApiError(error, 'orders.info.folderCouldNotBeCanceled');
            return of({ type: 'EMPTY' });
          }),
        ),
      ),
    );
  });

  // Remboursement d'une formule depuis le volet (GEN-6319) : endpoint souscription-aware. Même
  // gestion 200 / 207 (partiel = succès) / 4xx (showApiError) que le paiement.
  processSubscriptionRefund$ = createEffect(() => {
    return this.actions$.pipe(
      ofType(OrdersActions.processSubscriptionRefund),
      switchMap(({ request, orderId, orderItemId, level }) =>
        this.ordersService.refundOrderItemsAndSubscriptions(request).pipe(
          // Le client NSwag jette pour tout statut hors 200 (le 207 arrive par catchError) :
          // une réponse non jetée est donc toujours un succès.
          concatMap(() => [
            OrdersActions.processSubscriptionActionSuccess({ orderItemId, orderId, level, action: 'refund' }),
          ]),
          catchError((error: unknown) => {
            const isPartialSuccess = ApiException.isApiException(error) && error.status === 207;
            if (isPartialSuccess) {
              return [
                OrdersActions.processSubscriptionActionSuccess({ orderItemId, orderId, level, action: 'refund' }),
              ];
            }
            this.alertService.showApiError(error, 'orders.info.folderCouldNotBeRefunded');
            return of({ type: 'EMPTY' });
          }),
        ),
      ),
    );
  });

  // Annulation / remboursement de places d'une formule sur TOUTES ses séances : pipeline
  // `orders/operations`, une seule transaction. Pas de 207 ici — le contrat est tout ou rien, un
  // conflit revient en 409 porteur du plan, surfacé comme les autres 4xx via showApiError (traceId).
  //
  // Le libellé lu par l'opérateur vient de `errors.order-operation.*` dans `fr.json`, PAS du repli
  // passé ici : `showApiError` n'utilise le repli que si la réponse ne porte aucun errorCode —
  // sinon il retombe sur `detail`, le message anglais du backend. Tout errorCode que ce parcours
  // peut produire doit donc avoir sa clé FR.
  processSubscriptionSeatsCancel$ = createEffect(() => {
    return this.actions$.pipe(
      ofType(OrdersActions.processSubscriptionSeatsCancel),
      switchMap(({ request, orderId, orderItemId, level, action }) =>
        this.ordersOperationsService.cancel(request).pipe(
          concatMap(() => [
            OrdersActions.processSubscriptionActionSuccess({
              orderItemId,
              orderId,
              level,
              action,
              scope: OrdersActions.SUBSCRIPTION_SEATS_SCOPE,
            }),
          ]),
          catchError((error: unknown) => {
            this.alertService.showApiError(
              error,
              action === 'cancel'
                ? 'orders.info.formulaSeatsCouldNotBeCanceled'
                : 'orders.info.formulaSeatsCouldNotBeRefunded',
            );
            return of({ type: 'EMPTY' });
          }),
        ),
      ),
    );
  });

  // Succès annulation/remboursement souscription : toast contextualisé + rafraîchissement du volet
  // (loadOrderItems) + rechargement des paiements du dossier affiché (pour que l'onglet Paiement, piloté
  // par le store, se mette à jour sans re-sélection) + reset de l'action. Le flag de complétion (reducer)
  // déclenche la désélection du tableau Détail formule (GEN-6319).
  processSubscriptionActionSuccess$ = createEffect(() => {
    return this.actions$.pipe(
      ofType(OrdersActions.processSubscriptionActionSuccess),
      withLatestFrom(
        this.store.pipe(select(fromOrders.selectors.selectOrderItemsFilters)),
        this.store.pipe(select(fromOrders.selectors.getSelectedOrderItem)),
      ),
      concatMap(([{ orderItemId, orderId, action, scope }, filters, selectedFolder]) => {
        this.alertService.showCustom(SubscriptionActionToastKeys[scope ?? 'formula'][action], 'success');
        const actions: Action[] = [
          OrdersActions.setOrdersListFilters({ filters: [...filters] }),
          OrdersActions.loadOrderItems({ orderId, orderItemId }),
        ];
        if (selectedFolder?.id != null) {
          actions.push(OrdersActions.loadOrderItemPayments({ orderItemId: selectedFolder.id }));
        }
        actions.push(OrdersActions.resetOrderAction());
        return actions;
      }),
    );
  });

  processOrderPaymentSuccess$ = createEffect(() => {
    return this.actions$.pipe(
      ofType(OrdersActions.processOrderPaymentSuccess),
      withLatestFrom(
        this.store.pipe(select(fromOrders.selectors.selectOrderItemsFilters)),
        this.store.pipe(select(fromOrders.selectors.getSelectedOrderItem)),
      ),
      concatMap(([{ orderItemId, orderId, level, isMultipleFolders }, filters, selectedFolder]) => {
        const paymentSuccessMessage = 'orders.info.folderPaidSuccessfully';
        if (level === OrdersActionLevel.List) {
          this.alertService.showCustom(
            isMultipleFolders ? 'orders.info.yourPaymentMadeSuccessfully' : paymentSuccessMessage,
            'success',
          );
          return [
            OrdersActions.setOrdersListFilters({ filters: [...filters] }),
            OrdersActions.resetOrderAction(),
          ];
        } else {
          this.alertService.showCustom(
            level === OrdersActionLevel.OrderDetails
              ? 'orders.info.orderPaidSuccessfully'
              : paymentSuccessMessage,
            'success',
          );
          // Recharge les paiements du dossier affiché pour que l'onglet Paiement (piloté par le store)
          // se mette à jour immédiatement après l'encaissement, sans re-sélection du dossier (GEN-6319).
          const actions: Action[] = [
            OrdersActions.setOrdersListFilters({ filters: [...filters] }),
            OrdersActions.loadOrderItems({ orderId, orderItemId }),
          ];
          if (selectedFolder?.id != null) {
            actions.push(OrdersActions.loadOrderItemPayments({ orderItemId: selectedFolder.id }));
          }
          actions.push(OrdersActions.resetOrderAction());
          return actions;
        }
      }),
    );
  });

  processSeatPayment$ = createEffect(() => {
    return this.actions$.pipe(
      ofType(OrdersActions.processSeatPayment),
      switchMap(({ financialTransactionSeats, orderId, orderItemId, level, seatReference }) =>
        this.paymentService.payOrderSeats(financialTransactionSeats, orderItemId).pipe(
          concatMap((result) => {
            if (result?.isOperationSuccess) {
              return [
                OrdersActions.processSeatPaymentSuccess({
                  seatId: financialTransactionSeats?.seatIds?.[0],
                  orderId,
                  orderItemId,
                  level,
                  isMultipleSeats: financialTransactionSeats?.seatIds?.length > 1,
                  seatReference,
                }),
              ];
            } else {
              // Contrat legacy Operation (pas de ProblemDetails => pas de traceId possible).
              // TODO: basculer sur showApiError quand payOrderSeats sera migré UseCase.
              this.alertService.showCustom(
                this.translate.instant(
                  financialTransactionSeats?.seatIds.length > 1
                    ? 'orders.info.placeNumberCouldNotBePaid'
                    : 'orders.info.placesNumberCouldNotBePaid',
                  {
                    id:
                      financialTransactionSeats?.seatIds.length > 1
                        ? financialTransactionSeats?.seatIds?.[0]
                        : financialTransactionSeats?.seatIds.join(' - '),
                  },
                ),
                'danger',
              );
              return of({ type: 'EMPTY' });
            }
          }),
          catchError((err: unknown) => {
            this.alertService.showApiError(
              err,
              this.translate.instant(
                financialTransactionSeats?.seatIds.length > 1
                  ? 'orders.info.placeNumberCouldNotBePaid'
                  : 'orders.info.placesNumberCouldNotBePaid',
                {
                  id:
                    financialTransactionSeats?.seatIds.length > 1
                      ? financialTransactionSeats?.seatIds?.[0]
                      : financialTransactionSeats?.seatIds.join(' - '),
                },
              ),
            );
            return of({ type: 'EMPTY' });
          }),
        ),
      ),
    );
  });

  processSeatPaymentSuccess$ = createEffect(() => {
    return this.actions$.pipe(
      ofType(OrdersActions.processSeatPaymentSuccess),
      withLatestFrom(this.store.pipe(select(fromOrders.selectors.selectOrderItemsFilters))),
      concatMap(([{ seatId, orderId, orderItemId, level, isMultipleSeats, seatReference }, filters]) => {
        this.alertService.showCustom(
          isMultipleSeats
            ? 'orders.info.selectionPaidSuccessfully'
            : this.translate.instant('orders.info.placePaidSuccessfully', { seatReference: seatReference }),
          'success',
        );
        return [
          OrdersActions.setOrdersListFilters({ filters: [...filters] }),
          OrdersActions.loadOrderItems({
            orderId: orderId,
            orderItemId: orderItemId,
          }),
          OrdersActions.resetOrderAction(),
        ];
      }),
    );
  });

  processOrderCancel$ = createEffect(() => {
    return this.actions$.pipe(
      ofType(OrdersActions.processOrderCancel),
      switchMap(({ orderItemCancel, orderId, level }) =>
        this.ordersService.cancel(orderItemCancel).pipe(
          map(() =>
            OrdersActions.processOrderCancelSuccess({
              orderItemId: orderItemCancel?.orderItemIds?.[0],
              orderId,
              level,
              isMultipleFolders: orderItemCancel?.orderItemIds?.length > 1,
            }),
          ),
          catchError((error) => {
            this.alertService.showApiError(error);
            return of({ type: 'EMPTY' });
          }),
        ),
      ),
    );
  });

  processOrderCancelSuccess$ = createEffect(() => {
    return this.actions$.pipe(
      ofType(OrdersActions.processOrderCancelSuccess),
      withLatestFrom(this.store.pipe(select(fromOrders.selectors.selectOrderItemsFilters))),
      concatMap(([{ orderItemId, orderId, level, isMultipleFolders }, filters]) => {
        const folderSuccessMessage = 'orders.info.folderCanceledSuccessfully';
        if (level === OrdersActionLevel.List) {
          this.alertService.showCustom(
            isMultipleFolders ? 'orders.info.selectionCanceledSuccessfully' : folderSuccessMessage,
            'success',
          );
          return [
            OrdersActions.setOrdersListFilters({ filters: [...filters] }),
            OrdersActions.resetOrderAction(),
          ];
        } else {
          this.alertService.showCustom(
            level === OrdersActionLevel.OrderDetails
              ? 'orders.info.orderCanceledSuccessfully'
              : folderSuccessMessage,
            'success',
          );
          return [
            OrdersActions.setOrdersListFilters({ filters: [...filters] }),
            OrdersActions.loadOrderItems({
              orderId: orderId,
              orderItemId: orderItemId,
            }),
            OrdersActions.resetOrderAction(),
          ];
        }
      }),
    );
  });

  processOrderDocument$ = createEffect(() => {
    return this.actions$.pipe(
      ofType(OrdersActions.processOrderDocument),
      switchMap(({ request }) =>
        this.ordersService.generateDocument(request).pipe(
          concatMap((file: FileResponse) => [OrdersActions.processOrderDocumentSuccess({ file })]),
          catchError(() => of({ type: 'EMPTY' })),
        ),
      ),
    );
  });

  processOrderDocumentSuccess$ = createEffect(
    () => {
      return this.actions$.pipe(
        ofType(OrdersActions.processOrderDocumentSuccess),
        switchMap(({ file }) => {
          this.utils.saveToFileSystem(file.data, file?.fileName, file?.headers?.['content-type']);
          this.alertService.showCustom('orders.info.documentDownloadedSuccessfully', 'success');
          return [of(null)];
        }),
      );
    },
    { dispatch: false },
  );

  processSeatsCancel$ = createEffect(() => {
    return this.actions$.pipe(
      ofType(OrdersActions.processSeatsCancel),
      switchMap(({ orderArticles, orderSeatsCancel, level }) => {
        let cancelSingleArticleErrorMsg = '';
        let cancelSingleArticleSuccessMsg = '';
        if (orderSeatsCancel?.seatIds?.length === 1) {
          const Seat = (orderArticles?.orderArticleItems?.[0] as OrderArticleSeatResponseDto)?.seatInfos;
          if (Seat === undefined) {
            cancelSingleArticleErrorMsg = 'orders.info.productCouldNotBeCanceled';
            cancelSingleArticleSuccessMsg = 'orders.info.productCanceledSuccessfully';
          } else {
            cancelSingleArticleErrorMsg = this.translate.instant('orders.info.placeCodeCouldNotBeCanceled', {
              accessCode: Seat?.accessCode,
              rank: Seat?.rank,
              seatNumber: Seat?.seatNumber,
            });
            cancelSingleArticleSuccessMsg = this.translate.instant('orders.info.placeCodeCanceled', {
              accessCode: Seat?.accessCode,
              rank: Seat?.rank,
              seatNumber: Seat?.seatNumber,
            });
          }
        }

        return this.ordersSeatsService.cancel(orderSeatsCancel).pipe(
          concatMap((result) => {
            if (result?.isOperationSuccess) {
              return [
                OrdersActions.processSeatsCancelSuccess({
                  orderArticles: orderArticles,
                  level,
                  isMultipleseats: orderSeatsCancel?.seatIds?.length > 1,
                  seatReference: cancelSingleArticleSuccessMsg,
                }),
              ];
            } else {
              // Contrat legacy Operation (pas de ProblemDetails => pas de traceId possible).
              // TODO: basculer sur showApiError quand le cancel de places sera migré UseCase.
              const cancelMsg =
                orderSeatsCancel?.seatIds?.length === 1
                  ? `${cancelSingleArticleErrorMsg}`
                  : this.translate.instant('orders.info.placesIdsCouldNotBeCanceled', {
                      seatIds: orderSeatsCancel?.seatIds.join(' - '),
                    });
              this.alertService.showCustom(cancelMsg, 'danger');
              return of({ type: 'EMPTY' });
            }
          }),
          catchError((err: unknown) => {
            const cancelMsg =
              orderSeatsCancel?.seatIds?.length === 1
                ? `${cancelSingleArticleErrorMsg}`
                : this.translate.instant('orders.info.placesIdsCouldNotBeCanceled', {
                    seatIds: orderSeatsCancel?.seatIds.join(' - '),
                  });
            this.alertService.showApiError(err, cancelMsg);
            return of({ type: 'EMPTY' });
          }),
        );
      }),
    );
  });

  processSeatsCancelSuccess$ = createEffect(() => {
    return this.actions$.pipe(
      ofType(OrdersActions.processSeatsCancelSuccess),
      withLatestFrom(this.store.pipe(select(fromOrders.selectors.selectOrderItemsFilters))),
      concatMap(([{ orderArticles, isMultipleseats, seatReference }, filters]) => {
        const seatSuccessMessage = isMultipleseats
          ? 'orders.info.selectedPlacesCanceled'
          : `${seatReference}`;

        this.alertService.showCustom(seatSuccessMessage, 'success');
        return [
          OrdersActions.setOrdersListFilters({ filters: [...filters] }),
          OrdersActions.loadOrderItems({
            orderId: orderArticles.orderID,
            orderItemId: orderArticles.orderItemId,
          }),
          OrdersActions.resetOrderAction(),
        ];
      }),
    );
  });

  processPaymentEdit$ = createEffect(() => {
    return this.actions$.pipe(
      ofType(OrdersActions.processOrderPaymentEdit),
      withLatestFrom(this.store.pipe(select(fromOrders.selectors.getSelectedOrderItem))),
      switchMap(([{ paymentEditPayload, paymentType }, orderItem]) =>
        this.ordersService.updatePaymentItems(paymentEditPayload).pipe(
          concatMap((operation: Operation) => {
            if (operation?.isOperationSuccess) {
              this.alertService.showCustom(
                paymentType === PaymentOperationTypeEnumRequestDto.Payment
                  ? 'orders.info.paymentUpdatedSuccessfully'
                  : 'orders.info.refundUpdatedSuccessfully',
                'success',
              );
              return [
                fromOrders.actions.loadOrderItemPayments({ orderItemId: orderItem?.id }),
                OrdersActions.loadOrderItems({
                  orderId: orderItem?.orderId,
                  orderItemId: orderItem?.id,
                }),
                OrdersActions.resetOrderAction(),
              ];
            }
            // The endpoint answers with the legacy Operation contract, which carries a localized
            // business message but no errorCode and no traceId. That message is shown as is - the
            // translate pipe leaves an unknown key untouched - and the generic key covers the
            // responses that carry none.
            this.alertService.showCustom(
              operation?.errorMessage ||
                (paymentType === PaymentOperationTypeEnumRequestDto.Payment
                  ? 'orders.info.paymentCouldNotBeUpdated'
                  : 'orders.info.refundCouldNotBeUpdated'),
              'danger',
            );
            return [{ type: 'EMPTY' }];
          }),
          catchError((err: unknown) => {
            this.alertService.showApiError(
              err,
              paymentType === PaymentOperationTypeEnumRequestDto.Payment
                ? 'orders.info.paymentCouldNotBeUpdated'
                : 'orders.info.refundCouldNotBeUpdated',
            );
            return of({ type: 'EMPTY' });
          }),
        ),
      ),
    );
  });

  processEditSuccess$ = createEffect(() => {
    return this.actions$.pipe(
      ofType(OrdersActions.processOrderEditSuccess),
      withLatestFrom(this.store.pipe(select(fromOrders.selectors.selectOrderItemsFilters))),
      concatMap(([{ orderItemId, orderId, level }, filters]) => {
        if (level === OrdersActionLevel.List) {
          return [
            OrdersActions.setOrdersListFilters({ filters: [...filters] }),
            OrdersActions.resetOrderAction(),
          ];
        } else {
          return [
            OrdersActions.setOrdersListFilters({ filters: [...filters] }),
            OrdersActions.loadOrderItems({
              orderId: orderId,
              orderItemId: orderItemId,
            }),
            OrdersActions.resetOrderAction(),
          ];
        }
      }),
    );
  });

  refreshOrderCustomer$ = createEffect(() => {
    return this.actions$.pipe(
      ofType(OrdersActions.refreshOrderCustomer),
      withLatestFrom(this.store.pipe(select(fromOrders.selectors.selectOrderItemsFilters))),
      concatMap(([{ orderId, orderItemId }, filters]) => {
        return [
          OrdersActions.setOrdersListFilters({ filters: [...filters] }),
          OrdersActions.loadOrderItems({
            orderId: orderId,
            orderItemId: orderItemId,
          }),
          OrdersActions.resetOrderAction(),
        ];
      }),
    );
  });

  initFromCustomers$ = createEffect(() => {
    return this.actions$.pipe(
      ofType(OrdersActions.initFromCustomers),
      concatMap(({ customerId }) => [
        OrdersActions.setOrdersListFilters({
          filters: [
            {
              condition: OperatorTypeEnumRequestDto.Equal,
              field: CustomerPropertyEnumRequestDto.CustomerIdentifier,
              result: customerId,
              type: TableFilterTypeEnum.Custom,
            },
          ],
        }),
      ]),
    );
  });
}
